import React, { ReactElement, useEffect, useState } from 'react';
import { useQuery } from '@apollo/react-hooks';
import { Button, Flex, Text, Box, Image } from '@chakra-ui/core';
import { Document, Page } from 'react-pdf';
import { DownloadSVG } from 'components/Icons';
import { useIsThemeBreakPoint } from 'contexts/Breakpoints';
import openInNewTab from 'utilityFns/openInNewTab';
import researchDocumentImage from 'assets/images/researchDocumentLarge.png';
import { PdfButtonRow, PdfLoader } from 'pages/ReportPage/PDFViewer';
import { GET_BUCKET_CONTENTS, HIDOE_DATA_BOOK_SLUG, makeUrl } from './dataBookBucketQuery';

interface DataBookMainPdfViewerProps {
  year: number;
}

const DataBookMainPdfViewer: React.FC<DataBookMainPdfViewerProps> = ({ year }): ReactElement => {
  const desktop = useIsThemeBreakPoint(2);
  const [pageCount, setPageCount] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [tabWasOpened, setTabWasOpened] = useState(false);
  const [documentLoading, setDocumentLoading] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);

  const { data, loading } = useQuery(GET_BUCKET_CONTENTS, {
    variables: { slug: HIDOE_DATA_BOOK_SLUG, year },
    skip: !year,
  });

  const filename =
    data?.getBucketContents?.items?.find((i: { filename: string }): boolean => i.filename.includes('HIDOEDatabook'))
      ?.filename ?? null;
  const filePath = filename ? makeUrl(HIDOE_DATA_BOOK_SLUG, year, filename) : null;

  useEffect(() => {
    if (!filePath) {
      return;
    }
    setLoadFailed(false);
    setPageNumber(1);
    setPageCount(0);
  }, [filePath]);

  useEffect(() => {
    if (filePath && !desktop && !tabWasOpened) {
      openInNewTab(filePath);
      setTabWasOpened(true);
    }
  }, [filePath, desktop, tabWasOpened]);

  useEffect(() => {
    setTabWasOpened(false);
  }, [year]);

  const onLoadSuccess = ({ numPages }: { numPages: number }): void => {
    setDocumentLoading(false);
    setLoadFailed(false);
    setPageCount(numPages);
    setPageNumber(1);
  };

  const onFileLoadError = (): void => {
    setDocumentLoading(false);
    setLoadFailed(true);
  };

  const previousPage = (): void => {
    if (pageNumber <= 1) return;
    setPageNumber(pageNumber - 1);
  };

  const nextPage = (): void => {
    if (pageNumber >= pageCount) return;
    setPageNumber(pageNumber + 1);
  };

  if (loading) {
    return (
      <Flex direction="column" align="center" minHeight="300px" paddingTop="24px">
        <PdfLoader />
      </Flex>
    );
  }

  if (!loading && !filePath) {
    return (
      <Flex margin="4em" direction="column" alignItems="center">
        <Image width="100px" src={researchDocumentImage} alt="HIDOE Data Book" />
        <Text as="span" textAlign="center" color="textGray" marginTop="1em">
          No HIDOE Data Book PDF is listed for this school year.
        </Text>
      </Flex>
    );
  }

  if (!desktop) {
    return (
      <Flex margin="82px" direction="column" align="center" justify="center" minHeight="120px">
        <Text>Opened in new tab on mobile.</Text>
        <Button
          onClick={(): void => {
            if (filePath) openInNewTab(filePath);
          }}
          background="#4E4A58"
          color="white"
          paddingLeft="24px"
          paddingRight="24px"
          margin="12px"
        >
          <DownloadSVG color="white" />
        </Button>
      </Flex>
    );
  }

  if (loadFailed) {
    return (
      <Flex margin="4em" direction="column" alignItems="center">
        <Image width="100px" src={researchDocumentImage} alt="HIDOE Data Book" />
        <Text as="span" textAlign="center" color="textGray" marginTop="1em">
          Unable to load this PDF. Please try again or use the download links below.
        </Text>
      </Flex>
    );
  }

  return (
    <Flex direction="column" align="center" justify="flex-start" minHeight="300px" paddingTop="24px">
      {!documentLoading && (
        <PdfButtonRow
          previousPage={previousPage}
          nextPage={nextPage}
          pageNumber={pageNumber}
          pageCount={pageCount}
          downloadPath={filePath}
          report={HIDOE_DATA_BOOK_SLUG}
        />
      )}
      <Box width="100%" display="flex" flexDirection="column" alignItems="center">
        <Document
          file={filePath}
          onLoadSuccess={onLoadSuccess}
          onSourceError={onFileLoadError}
          onLoadError={onFileLoadError}
          loading={<PdfLoader />}
          onSourceSuccess={(): void => setDocumentLoading(true)}
        >
          <Page pageNumber={pageNumber} renderTextLayer={false} renderAnnotationLayer={false} />
        </Document>
      </Box>
    </Flex>
  );
};

export default DataBookMainPdfViewer;
